// Search result utilities

export type PartDetailResultGroup = PartDetailResultTransformed & {
    PartResults: PartResult[];
};

export type DistributorResultGroup = PartResultDistributorTransformed & { PartResults: PartResult[] };

const emptyManufacturer: PartResultManufacturer = { Id: 0, Name: "", DisplayName: "", VirtualPath: "" };

export function transformSearchContext(searchContext: SearchContext): SearchContextTransformed | undefined {
    if (!searchContext.IsValidSearch) return;
    const { Results: searchResults, ...other } = searchContext;

    return { ...other, Results: transformSearchResults(searchResults) };
}

function transformDistributor(distributorResult: DistributorSearchResult): PartResultDistributorTransformed {
    const { Distributor, TotalResultCount, ExactResultCount, ExactMatchCount, ViewAllUrl, ViewAllText } =
        distributorResult;
    return {
        ...Distributor,
        TotalResultCount,
        ExactResultCount,
        ExactMatchCount,
        ViewAllUrl,
        ViewAllText,
    };
}

type OtherKeyRecord = { Key: string; ManufacturerId: number; ManufacturerName: string };

/**
 * Do search result data cleanup and sorting
 * TODO: this function's output should essentially be what the API outputs
 * 1. Create temporary part detail records from part records
 * 2. Map non-canonical manufacturers parts to canonical manufacturer parts
 * 3. Output manufacturers and distributors since we have them anyway
 */
export function transformSearchResults(searchResults: SearchResults): SearchResultsTransformed {
    const otherKeyRecordMap = new Map<string, OtherKeyRecord>();
    const partDetailMap = new Map<string, PartDetailResult>();
    const manufacturerMap = new Map<number, PartResultManufacturer>();
    const distributors: PartResultDistributorTransformed[] = [];
    const partResults: PartResult[] = [];

    // loop through part detail results to build manufacturer ID and part key mappings
    searchResults.PartDetailResults.forEach((partDetail) => {
        if (partDetail.OtherManufacturerIds.length > 0) {
            const record: OtherKeyRecord = {
                Key: partDetail.Key,
                ManufacturerId: partDetail.ManufacturerId,
                ManufacturerName: partDetail.ManufacturerName,
            };

            partDetail.OtherManufacturerIds.forEach((otherId) => {
                const otherKey = `${otherId}-${partDetail.PartNumberScrubbedNonMeaningful}`;
                otherKeyRecordMap.set(otherKey, record);
            });
        }
    });

    // get all manufacturers and distributors
    searchResults.DistributorResults.forEach((distributorResult) => {
        distributors.push(transformDistributor(distributorResult));
        distributorResult.Results.forEach((partResult) => {
            const mfrId = partResult.Manufacturer.Id;
            if (!manufacturerMap.has(mfrId)) manufacturerMap.set(mfrId, partResult.Manufacturer);
        });
    });

    // Populate manufacturers for catalog-only parts (no distributor offers) from part detail results,
    // since those manufacturers won't be present in the map built from distributor results above.
    searchResults.PartDetailResults.forEach((partDetail) => {
        if (!manufacturerMap.has(partDetail.ManufacturerId)) {
            manufacturerMap.set(partDetail.ManufacturerId, {
                Id: partDetail.ManufacturerId,
                Name: partDetail.ManufacturerName,
                DisplayName: partDetail.ManufacturerName,
                VirtualPath: partDetail.ManufacturerVirtualPath,
            });
        }
    });

    // make part detail map with fixed part keys and manufacturers
    // part detail results will be empty on first search
    searchResults.PartDetailResults.forEach((partDetail) => {
        const record = otherKeyRecordMap.get(partDetail.Key);
        if (record) {
            partDetailMap.set(record.Key, { ...partDetail, ...record });
        } else {
            partDetailMap.set(partDetail.Key, partDetail);
        }
    });

    searchResults.DistributorResults.forEach((distributorResult) => {
        distributorResult.Results.forEach((partResult) => {
            // handle if this specific part key was mapped to another manufacturer
            const record: OtherKeyRecord = otherKeyRecordMap.get(partResult.Key) ?? {
                Key: partResult.Key,
                ManufacturerId: partResult.Manufacturer.Id,
                ManufacturerName: partResult.Manufacturer.Name,
            };

            // make temporary part detail results if needed
            if (!partDetailMap.has(record.Key)) {
                const tempPartDetail: PartDetailResult = {
                    ...partResult,
                    ...record,
                    ManufacturerVirtualPath: partResult.Manufacturer.VirtualPath,
                    PartNumberScrubbedNonMeaningful: partResult.ScrubbedPartNumberNonMeaningful,
                    PartId: 0,
                    OtherManufacturerIds: [],
                    IsSelfHostedImage: false,
                    IsAffectedByTariff: false,
                    CategoryHierarchy: [],
                    LifecycleRisk: -1,
                    SupplyChainRisk: -1,
                    PartResults: [],
                };
                partDetailMap.set(record.Key, tempPartDetail);
            }

            partResults.push({
                ...partResult,
                Key: record.Key,
                Manufacturer: manufacturerMap.get(record.ManufacturerId) ?? partResult.Manufacturer,
                DistributorSuppliedManufacturer: partResult.Manufacturer,
            });
        });
    });

    distributors.sort((distributorA, distributorB) => distributorA.Name.localeCompare(distributorB.Name));

    // try to add full manufacturer object to main part detail result
    let mainResult: PartDetailResultTransformed | undefined = undefined;
    if (searchResults.MainPartDetailResult) {
        const mfrId = searchResults.MainPartDetailResult.ManufacturerId;
        mainResult = { ...searchResults.MainPartDetailResult, Manufacturer: manufacturerMap.get(mfrId) };
    }

    return {
        IsComplete: searchResults.IsComplete,
        GoogleAdId: searchResults.GoogleAdId,
        ManufacturerMap: manufacturerMap,
        Distributors: distributors,
        PartDetailResults: Array.from(partDetailMap.values()),
        MainPartDetailResult: mainResult,
        PartResults: partResults,
    };
}

/**
 * Group search results by product (part) and sort the output for the "by part" view.
 *
 * @param searchText the raw query string, used to score exact part-number matches
 * @param searchResults transformed search results containing part and part-detail records
 * @returns part-detail groups, each with its distributor offers, sorted by relevance
 */
export function groupResultsByPart(
    searchText: string,
    searchResults: SearchResultsTransformed
): PartDetailResultGroup[] {
    const partResultsByKey = new Map<string, PartResult[]>();
    // per-part totals used for sorting, keyed by part key
    const matchScore = new Map<string, number>();
    const distributorsWithStock = new Map<string, number>();
    const totalStock = new Map<string, number>();
    const upperSearchText = searchText.trim().toUpperCase();

    searchResults.PartResults.forEach((partResult) => {
        const key = partResult.Key;
        const partResults = partResultsByKey.get(key);

        if (partResults) {
            partResults.push(partResult);
        } else {
            partResultsByKey.set(key, [partResult]);
            // Prioritize exact part number and search text equality, then IsExactMatch property
            matchScore.set(
                key,
                partResult.PartNumber.toUpperCase() == upperSearchText ? 2 : partResult.IsExactMatch ? 1 : 0
            );
            distributorsWithStock.set(key, 0);
            totalStock.set(key, 0);
        }

        if (partResult.QuantityOnHand) {
            totalStock.set(key, (totalStock.get(key) ?? 0) + partResult.QuantityOnHand);
            distributorsWithStock.set(key, (distributorsWithStock.get(key) ?? 0) + 1);
        }
    });

    const output: PartDetailResultGroup[] = searchResults.PartDetailResults.map((partDetail) => {
        // Set match score for catalog-only parts not seen in any distributor result
        if (!matchScore.has(partDetail.Key)) {
            matchScore.set(partDetail.Key, partDetail.PartNumber.toUpperCase() === upperSearchText ? 2 : 0);
        }
        return {
            ...partDetail,
            // manufacturer object makes the part detail satisfy the CommonPart type for notifications and charts
            Manufacturer: searchResults.ManufacturerMap.get(partDetail.ManufacturerId) ?? emptyManufacturer,
            PartResults: partResultsByKey.get(partDetail.Key) ?? [],
        };
    });

    // Sorts by: exact match first, then has offers, then # of distributors (desc), then total stock (desc)
    // Keeping exact match as the primary key groups exact matches together so that an exact match with
    // no distributor offers sorts right after exact matches with stock instead of below non-exact offers.
    // Sorting (relevance) logic belongs in the API layer, but this is a temporary workaround.
    output.sort((partA, partB) => {
        const aHasOffers = partA.PartResults.length > 0 ? 1 : 0;
        const bHasOffers = partB.PartResults.length > 0 ? 1 : 0;
        let result = (matchScore.get(partB.Key) ?? 0) - (matchScore.get(partA.Key) ?? 0);
        if (result == 0) result = bHasOffers - aHasOffers;
        if (result == 0)
            result = (distributorsWithStock.get(partB.Key) ?? 0) - (distributorsWithStock.get(partA.Key) ?? 0);
        if (result == 0) result = (totalStock.get(partB.Key) ?? 0) - (totalStock.get(partA.Key) ?? 0);

        return result;
    });

    return output;
}

/**
 * Produce a deterministic float in [0, 1) from an arbitrary string.
 * Used to give each distributor a stable per-search sort position that is
 * identical on SSR and client, eliminating hydration mismatches.
 */
function seededRandom(seed: string): number {
    let h = 0x811c9dc5; // FNV-1a 32-bit offset basis
    for (let i = 0; i < seed.length; i++) {
        h ^= seed.charCodeAt(i);
        h = Math.imul(h, 0x01000193) >>> 0; // FNV prime, keep unsigned
    }
    return h / 0x100000000;
}

// Group distributors and sort: sponsors first, then stable-random order seeded by searchId.
// Passing null skips sorting — used as a fallback when the inject context is unavailable.
export function groupResultsByDistributor(
    searchResults: SearchResultsTransformed,
    searchId: string | null
): DistributorResultGroup[] {
    const output: DistributorResultGroup[] = searchResults.Distributors.map((distributor) => {
        return {
            ...distributor,
            PartResults: searchResults.PartResults.filter((partResult) => partResult.Distributor.Id == distributor.Id),
        };
    });

    if (!searchId) return output;

    // Stable per-search random order: seeded by searchId+distributorId so SSR and
    // client produce the identical ordering, eliminating hydration mismatches.
    const seededOrder = new Map<number, number>(
        output.map((result) => [result.Id, seededRandom(searchId + ":" + result.Id)])
    );

    output.sort((distA, distB) => {
        const [isSponsorA, isSponsorB] = [distA.IsSponsor ? 1 : 0, distB.IsSponsor ? 1 : 0];
        let result = isSponsorB - isSponsorA;
        if (result == 0) result = (seededOrder.get(distA.Id) ?? 0) - (seededOrder.get(distB.Id) ?? 0);
        return result;
    });

    return output;
}
